import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Table,
  Typography,
  Tag,
  Space,
  Avatar,
  Button,
  Modal,
  Select,
  Switch,
  Form,
  App as AntApp,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  TeamOutlined,
  SafetyCertificateOutlined,
  UserAddOutlined,
  SearchOutlined,
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { usersApi, AdminUser, UsersStats, UserUpdatePayload } from '../../api/users';

const { Title, Text } = Typography;

const ROLE_OPTIONS = [
  { value: 'user', label: 'Житель', color: 'default' },
  { value: 'operator', label: 'Оператор', color: 'blue' },
  { value: 'moderator', label: 'Модератор', color: 'cyan' },
  { value: 'admin', label: 'Администратор', color: 'red' },
];

const roleInfo = (role: string) => ROLE_OPTIONS.find((r) => r.value === role) ?? { label: role, color: 'default' };

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatPhone = (phone: string | null) => {
  if (!phone) return '—';
  const p = phone.replace(/\D/g, '');
  if (p.length === 11) return `+${p[0]} (${p.slice(1, 4)}) ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}`;
  return phone;
};

const UsersList: React.FC = () => {
  const { message } = AntApp.useApp();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<UsersStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>();
  const [pagination, setPagination] = useState({ page: 1, size: 20, total: 0 });

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [form] = Form.useForm();

  const fetchStats = useCallback(async () => {
    try {
      const res = await usersApi.stats();
      setStats(res.data);
    } catch (e) {
      // silent — main table is still useful
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersApi.list({
        search: search || undefined,
        role: roleFilter,
        is_active: activeFilter,
        page: pagination.page,
        size: pagination.size,
      });
      setUsers(res.data.items);
      setPagination((p) => ({ ...p, total: res.data.total }));
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, activeFilter, pagination.page, pagination.size, message]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openEdit = (user: AdminUser) => {
    setEditing(user);
    form.setFieldsValue({
      role: user.role,
      is_active: user.is_active,
      full_name: user.full_name ?? '',
      district: user.district ?? '',
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const values = (await form.validateFields()) as UserUpdatePayload;
      await usersApi.update(editing.id, values);
      message.success('Пользователь обновлён');
      setEditOpen(false);
      fetchUsers();
      fetchStats();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Ошибка при сохранении');
    }
  };

  const toggleActive = async (user: AdminUser) => {
    try {
      await usersApi.update(user.id, { is_active: !user.is_active });
      message.success(user.is_active ? 'Пользователь заблокирован' : 'Пользователь разблокирован');
      fetchUsers();
      fetchStats();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка');
    }
  };

  const deleteUser = async (user: AdminUser) => {
    try {
      await usersApi.delete(user.id);
      message.success('Пользователь удалён');
      fetchUsers();
      fetchStats();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const columns: ColumnsType<AdminUser> = [
    {
      title: 'Пользователь',
      key: 'user',
      render: (_, u) => (
        <Space>
          <Avatar size={36} icon={<UserOutlined />} style={{ background: 'var(--l-accent)' }} />
          <div>
            <Text strong style={{ display: 'block' }}>{u.full_name || 'Без имени'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{formatPhone(u.phone)}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'ИИН / eGov',
      key: 'egov',
      width: 160,
      render: (_, u) =>
        u.egov_verified ? (
          <Tooltip title={u.iin}>
            <Tag color="green" icon={<SafetyCertificateOutlined />}>Верифицирован</Tag>
          </Tooltip>
        ) : (
          <Tag color="default">Не верифицирован</Tag>
        ),
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      width: 140,
      render: (role: string) => {
        const info = roleInfo(role);
        return <Tag>{info.label}</Tag>;
      },
    },
    {
      title: 'Район',
      dataIndex: 'district',
      key: 'district',
      width: 140,
      render: (d) => d || <Text type="secondary">—</Text>,
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 110,
      render: (active: boolean) =>
        active ? <Tag color="green">Активен</Tag> : <Tag color="red">Заблокирован</Tag>,
    },
    {
      title: 'Регистрация',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: formatDate,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 160,
      render: (_, u) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(u)} />
          </Tooltip>
          <Tooltip title={u.is_active ? 'Заблокировать' : 'Разблокировать'}>
            <Button
              type="text"
              icon={u.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
              danger={u.is_active}
              onClick={() => toggleActive(u)}
            />
          </Tooltip>
          <Popconfirm
            title="Удалить пользователя?"
            description="Это действие нельзя отменить."
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteUser(u)}
          >
            <Tooltip title="Удалить">
              <Button type="text" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (p: TablePaginationConfig) => {
    setPagination({ page: p.current || 1, size: p.pageSize || 20, total: pagination.total });
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Пользователи</Title>
        <Text type="secondary">Зарегистрированные жители города Аксай</Text>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Всего"
              value={stats?.total ?? 0}
              prefix={<TeamOutlined style={{ color: 'var(--l-accent)' }} />}
              valueStyle={{ color: 'var(--l-ink)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card style={{ }}>
            <Statistic
              title="eGov верифицированы"
              value={stats?.egov_verified ?? 0}
              prefix={<SafetyCertificateOutlined style={{ color: 'var(--l-ok)' }} />}
              valueStyle={{ color: '#389e0d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card style={{ }}>
            <Statistic
              title="Активные"
              value={stats?.active ?? 0}
              valueStyle={{ color: 'var(--l-accent)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card style={{ }}>
            <Statistic
              title="Новых за 7 дней"
              value={stats?.new_last_7_days ?? 0}
              prefix={<UserAddOutlined style={{ color: 'var(--l-warn)' }} />}
              valueStyle={{ color: '#d48806' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap>
          <Input
            placeholder="Поиск по имени, телефону, ИИН"
            prefix={<SearchOutlined style={{ color: 'var(--l-faint)' }} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            allowClear
            style={{ width: 320 }}
          />
          <Select
            placeholder="Роль"
            allowClear
            value={roleFilter}
            onChange={(v) => {
              setRoleFilter(v);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            style={{ width: 180 }}
            options={ROLE_OPTIONS.map((r) => ({ value: r.value, label: r.label }))}
          />
          <Select
            placeholder="Статус"
            allowClear
            value={activeFilter === undefined ? undefined : activeFilter ? 'active' : 'blocked'}
            onChange={(v) => {
              setActiveFilter(v === undefined ? undefined : v === 'active');
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            style={{ width: 160 }}
            options={[
              { value: 'active', label: 'Активные' },
              { value: 'blocked', label: 'Заблокированные' },
            ]}
          />
        </Space>
      </Card>

      <Card bodyStyle={{ padding: 0 }}>
        <Table<AdminUser>
          rowKey="id"
          columns={columns}
          dataSource={users}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.size,
            total: pagination.total,
            showTotal: (t) => `Всего: ${t}`,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
          }}
          onChange={handleTableChange}
          locale={{ emptyText: 'Пользователи не найдены' }}
        />
      </Card>

      <Modal
        title={editing ? `Редактировать: ${editing.full_name || editing.phone}` : 'Редактировать'}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={saveEdit}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Имя" name="full_name">
            <Input placeholder="Полное имя" />
          </Form.Item>
          <Form.Item label="Роль" name="role" rules={[{ required: true }]}>
            <Select options={ROLE_OPTIONS.map((r) => ({ value: r.value, label: r.label }))} />
          </Form.Item>
          <Form.Item label="Район" name="district">
            <Input placeholder="Район проживания" />
          </Form.Item>
          <Form.Item label="Активен" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersList;
